import { useNavigation } from '@/shared/hooks/useNavigation'
import { MobileSheet } from '@/shared/ui/MobileSheet'
import { ResourceModuleIcon } from '@/shared/ui/ResourceModuleIcon'
import { RewardPill } from '@/shared/ui/RewardPill'

import type { GuidanceAction } from '../model/types'

interface BlockGuidanceSheetProps {
  open: boolean
  onClose: () => void
  /** Assunto da explicação — vira também o nome acessível do diálogo. */
  title: string
  actions: GuidanceAction[]
}

/**
 * Quanto rende, dito na forma que a regra permite.
 *
 * Quiz paga por acerto, então o número solto mentiria: `ProgressResource`
 * calcula `point_reward * correct_questions_size`. A pílula é a mesma nos dois
 * casos; o que muda é a linha que a explica.
 */
const RewardLine = ({ reward }: { reward: GuidanceAction['reward'] }) => {
  if (!reward) return null

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {/* `xp`, não `coins`: ponto e moeda são métricas distintas no DS. */}
      <RewardPill type="xp" value={reward.points} compact />
      {reward.per === 'correct-answer' && (
        <span className="text-2xs font-semibold text-gray-700">
          por questão correta
        </span>
      )}
    </span>
  )
}

const GuidanceRow = ({
  action,
  onNavigate
}: {
  action: GuidanceAction
  onNavigate: (to: string) => void
}) => (
  <button
    type="button"
    onClick={() => onNavigate(action.to)}
    className="flex w-full cursor-pointer items-start gap-3 rounded-[0.625rem] border border-solid border-gray-100 bg-white px-3 py-2.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
  >
    <ResourceModuleIcon
      resourceType={action.resourceType}
      context="objectives"
      size={20}
      tileSize={44}
    />

    {/*
      MASTER §7 L2 admite **um** elemento fixo ao lado de uma linha de texto, e
      o tile ocupa essa vaga. Por isso não há seta à direita — o `<button>`
      inteiro já é a afordância — e a pílula desce para baixo do texto em vez de
      virar terceira coluna. Com a seta, a coluna de texto caía a ~233px a
      375px, abaixo do piso de ACCESSIBILITY §2.4. O precedente de forma,
      `ObjectiveDetailSheet`, também não tem seta na linha.
    */}
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-sm font-bold leading-snug text-gray-900">
        {action.title}
      </span>
      {action.instruction && (
        <span className="text-xs leading-snug text-gray-700">
          {action.instruction}
        </span>
      )}
      <RewardLine reward={action.reward} />
    </span>
  </button>
)

/**
 * A explicação de como preencher um bloco vazio da home.
 *
 * Compõe `MobileSheet`, que já entrega `role="dialog"`, `aria-modal`,
 * `aria-label` derivado do título, trava de scroll do body e fechamento por
 * Escape e backdrop — nada disso é reimplementado aqui.
 */
export const BlockGuidanceSheet = ({
  open,
  onClose,
  title,
  actions
}: BlockGuidanceSheetProps) => {
  const { navigateTo } = useNavigation()

  // Sheet sem ação é sheet vazio. Quem decide não oferecer a porta é
  // `useBlockGuidance`; esta guarda existe para que um call-site distraído não
  // consiga abrir um diálogo sem conteúdo.
  if (actions.length === 0) return null

  const handleNavigate = (to: string) => {
    onClose()
    navigateTo(to)
  }

  return (
    <MobileSheet open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-2">
        {actions.map(action => (
          <GuidanceRow
            key={action.key}
            action={action}
            onNavigate={handleNavigate}
          />
        ))}
      </div>
    </MobileSheet>
  )
}
